import { Component, type ErrorInfo, type ReactNode } from 'react';
import { logger } from '@/lib/logger';

// ─── Types ───────────────────────────────────────────────────────────

export type ErrorFallbackProps = {
  error: Error;
  resetError: () => void;
  featureName?: string;
};

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** Human-readable feature name shown in the fallback UI */
  featureName?: string;
  /** Custom fallback component; receives error + reset callback */
  fallback?: React.ComponentType<ErrorFallbackProps>;
  /** Called when an error is caught (logging, telemetry, etc.) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
};

type ErrorBoundaryState = {
  error: Error | null;
};

// ─── Default Fallback ────────────────────────────────────────────────

export function DefaultErrorFallback({ error, resetError, featureName }: ErrorFallbackProps) {
  return (
    <div
      role="alert"
      className="flex items-center justify-center rounded-lg border border-destructive/30 bg-destructive/5 p-6"
    >
      <div className="max-w-md space-y-3 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-destructive">
          Fehler
        </p>
        <h2 className="text-lg font-semibold text-foreground">
          {featureName
            ? `${featureName} konnte nicht geladen werden.`
            : 'Etwas ist schiefgelaufen.'}
        </h2>
        <p className="text-sm text-muted-foreground">
          {error.message || 'Ein unbekannter Fehler ist aufgetreten.'}
        </p>
        <button
          type="button"
          onClick={resetError}
          className="inline-flex items-center justify-center rounded-md bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          Erneut versuchen
        </button>
      </div>
    </div>
  );
}

// ─── ErrorBoundary Class ─────────────────────────────────────────────

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const context = this.props.featureName ?? 'ErrorBoundary';
    logger.error(context, 'Unhandled UI error', { error: error.message, componentStack: errorInfo.componentStack });
    this.props.onError?.(error, errorInfo);
  }

  resetError = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (error) {
      const Fallback = this.props.fallback ?? DefaultErrorFallback;
      return (
        <Fallback
          error={error}
          resetError={this.resetError}
          featureName={this.props.featureName}
        />
      );
    }

    return this.props.children;
  }
}
